// Firebase codes are mapped, never shown verbatim: they distinguish "no such user" from
// "wrong password", which would disclose which staff emails exist. Shared by every screen
// that calls Firebase sign-up/sign-in directly (invite acceptance, self-serve sign-up) so
// there is exactly one place this collapsing happens, not two independently-drifting copies.
export function friendlyAuthError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? "";
  if (code === "auth/email-already-in-use") return "That address already has an account — sign in instead.";
  if (code === "auth/weak-password") return "Use at least 6 characters.";
  if (code === "auth/too-many-requests") return "Too many attempts. Please wait a minute and try again.";
  if (code === "auth/invalid-email") return "That doesn't look like a valid email address.";
  // Only the codes Firebase actually uses for a rejected credential map to the credential
  // message, and they collapse into one: auth/user-not-found vs auth/wrong-password would
  // otherwise disclose which staff emails exist. Anything else — a dropped connection mid
  // sign-UP, an SDK misconfiguration — is not the user's password being wrong, and saying so
  // sends them off retyping a password that was fine.
  if (code === "auth/wrong-password" || code === "auth/user-not-found" ||
      code === "auth/invalid-credential" || code === "auth/invalid-login-credentials") {
    return "Wrong email or password.";
  }
  if (code === "auth/network-request-failed") return "No connection. Check your signal and try again.";
  if (code === "auth/popup-blocked") return "Allow popups for this site, then try Google again.";
  if (code === "auth/operation-not-allowed") return "Google sign-in is not enabled for this app.";
  return "Something went wrong. Please try again.";
}

// auth/account-exists-with-different-credential means this email already has a password
// user. The caller must sign in with that password and link Google onto the same uid.
// Returning "" (not null) means the code matched but Firebase did not include an email.
export function googleLinkEmail(e: unknown): string | null {
  if ((e as { code?: string })?.code !== "auth/account-exists-with-different-credential") return null;
  const email = (e as { email?: string }).email;
  return typeof email === "string" ? email : "";
}

// Closing the popup is a cancel, not a failure. A message here would look like a broken button.
export function isGoogleDismissed(e: unknown): boolean {
  const code = (e as { code?: string })?.code;
  return code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request";
}
